
import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const XDRUseCasesSection: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold mb-5" id="use-cases">Use Cases for AI-XDR</h2>
      <p className="text-lg text-gray-700 mb-6">
        AI-based XDR solutions excel across a wide range of security scenarios. Here are some of the most 
        impactful use cases where organizations are seeing significant benefits:
      </p>
      
      <div className="space-y-6 mb-10">
        <Tabs defaultValue="zero-day" className="w-full">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 mb-4">
            <TabsTrigger value="zero-day">Zero-Day Threats</TabsTrigger>
            <TabsTrigger value="insider">Insider Threats</TabsTrigger>
            <TabsTrigger value="remote">Remote Workforce</TabsTrigger>
            <TabsTrigger value="cloud">Cloud Protection</TabsTrigger>
          </TabsList>
          <div className="rounded-md border p-6">
            <TabsContent value="zero-day" className="mt-0">
              <h3 className="text-xl font-bold mb-4">Detecting Zero-Day Threats</h3>
              <p className="mb-4">
                AI-XDR solutions excel at identifying previously unknown threats by analyzing behavioral 
                patterns rather than relying on known signatures. This capability is crucial for detecting 
                zero-day exploits that bypass traditional security measures.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
                <h4 className="font-semibold mb-2">Example:</h4>
                <p>
                  A financial services firm's AI-XDR system detected unusual process execution chains on multiple 
                  endpoints. The behavior matched no known malware signature, but the AI identified it as anomalous 
                  based on learned normal patterns. Investigation revealed a zero-day exploit in a widely used 
                  financial software component. The organization contained the threat before data exfiltration 
                  occurred, while competitors using traditional security tools suffered significant breaches.
                </p>
              </div>
              <p>
                <strong>Key AI capabilities:</strong> Anomaly detection, behavioral analysis, threat 
                hunting algorithms
              </p>
            </TabsContent>
            
            <TabsContent value="insider" className="mt-0">
              <h3 className="text-xl font-bold mb-4">Identifying Insider Threats</h3>
              <p className="mb-4">
                AI-powered XDR establishes baselines of normal user behavior, making it possible to detect 
                subtle changes that might indicate an insider threat - whether malicious or accidental.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
                <h4 className="font-semibold mb-2">Example:</h4>
                <p>
                  An AI-XDR deployment at a pharmaceutical company identified a research scientist accessing 
                  proprietary formulation data outside normal working hours and downloading unusually large volumes 
                  of files. The system flagged this as anomalous because it deviated from the user's established 
                  patterns and from peer group behavior. Investigation revealed the employee was preparing to join 
                  a competitor and exfiltrate intellectual property worth millions.
                </p>
              </div>
              <p>
                <strong>Key AI capabilities:</strong> User and entity behavior analytics (UEBA), peer group 
                analysis, activity baselining
              </p>
            </TabsContent>
            
            <TabsContent value="remote" className="mt-0">
              <h3 className="text-xl font-bold mb-4">Securing Remote Workforces</h3>
              <p className="mb-4">
                With distributed workforces now standard, AI-XDR provides consistent security regardless of 
                user location, adapting to varied network environments while maintaining protection.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
                <h4 className="font-semibold mb-2">Example:</h4>
                <p>
                  A global consulting firm uses AI-XDR to protect thousands of consultants working from client sites, 
                  home offices, and public spaces. When an executive's credentials were compromised via a hotel WiFi 
                  network, the AI-XDR system detected the unusual login location combined with atypical system access 
                  patterns. It automatically isolated the compromised device while alerting the security team, 
                  preventing attackers from pivoting to corporate resources.
                </p>
              </div>
              <p>
                <strong>Key AI capabilities:</strong> Context-aware authentication, network traffic analysis, 
                device risk scoring
              </p>
            </TabsContent>
            
            <TabsContent value="cloud" className="mt-0">
              <h3 className="text-xl font-bold mb-4">Cloud Infrastructure Protection</h3>
              <p className="mb-4">
                AI-XDR solutions provide visibility into cloud environments that traditional tools miss, 
                detecting misconfigurations, unusual access patterns, and threats specific to cloud workloads.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
                <h4 className="font-semibold mb-2">Example:</h4>
                <p>
                  A retail organization's AI-XDR platform detected unusual API calls to their cloud storage from an 
                  authorized application during normal business hours. While traditional security tools saw nothing 
                  suspicious, the AI identified anomalous patterns in the API request frequency and data access. 
                  Investigation revealed a compromised development environment was being used to systematically 
                  extract customer data. The attack was stopped before significant data loss occurred.
                </p>
              </div>
              <p>
                <strong>Key AI capabilities:</strong> Cloud-native monitoring, API analysis, configuration 
                drift detection
              </p>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default XDRUseCasesSection;
